import React, { useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { Link, Typography } from '@mui/material';

import { translate as t } from 'hooks/useTranslations';
import ListSection from 'components/_layouts/ListSection';
import useIssuedManual from 'views/App/Portal/Policy/IssuedManuals/_data_sources/useIssuedManual';
import { ManualToCItem } from 'views/App/Portal/Policy/_types/manuals/draft';
import Loading from 'views/App/Portal/Loading';
import { Download } from '@mui/icons-material';
import { ActionMenu } from 'components/_atoms';
import { IssuedManualsContext } from '../context';
import TakeoverLayout from '_lex/_layouts/TakeoverLayout';
import ExportModal from '../_components/ExportModal';

const columnHelper = createColumnHelper<ManualToCItem>();

const IssuedManualsToC: React.FC = () => {
  const { id } = useParams();
  const manualQuery = useIssuedManual(id);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [selectedContent, setSelectedContent] = useState<ManualToCItem | null>(null);

  const handleExportClick = (item: ManualToCItem) => {
    setSelectedContent(item);
    setExportModalOpen(true);
  };

  const columns = [
    columnHelper.accessor('title', {
      cell: (info) => (
        <Link href={`./${info.row.original.contentType}/${info.row.original.id}`}>{info.getValue()}</Link>
      ),
      size: 9999,
    }),
    columnHelper.display({
      id: 'actions',
      cell: (info) =>
        info.row.original.contentType === 'preface' ? (
          <ActionMenu
            id={`action_menu_${info.row.original.id}`}
            items={[
              {
                key: `download_${info.row.original.id}`,
                icon: <Download sx={{ marginTop: 1 }} />,
                label: t('policy.issuedManuals.actions.pdf'),
                onClick: () => handleExportClick(info.row.original),
              },
            ]}
          />
        ) : null,
      header: () => '',
      size: 60,
    }),
  ];

  const tocItems = useMemo(() => {
    let toc: ManualToCItem[] = [];
    if (manualQuery.data && manualQuery.data.prefaces) {
      toc = [
        ...toc,
        ...manualQuery.data.prefaces.map(
          (x) =>
            ({
              id: x.id,
              title: ['Preface', x.title].join(': '),
              contentType: 'preface',
            }) as ManualToCItem
        ),
      ];
    }
    if (manualQuery.data && manualQuery.data.chapters) {
      toc = [
        ...toc,
        ...manualQuery.data.chapters.map(
          (x) =>
            ({
              id: x.id,
              title: [x.key, x.title].join(': '),
              contentType: 'chapter',
            }) as ManualToCItem
        ),
      ];
    }
    return toc;
  }, [manualQuery.data]);

  React.useEffect(() => {
    if (manualQuery.data && manualQuery.data.chapters) {
      manualQuery.data.chapters.forEach((chapter) => {
        if (chapter.policies) {
          chapter.policies.forEach((p) => {
            let hasXref = false;
            let hasAttach = false;
            if (p.sections) {
              p.sections.forEach((s) => {
                if (/xref/.test(s.content)) {
                  hasXref = true;
                }
                if (/s3:/.test(s.content)) {
                  hasAttach = true;
                }
                if (s.subSections) {
                  s.subSections.forEach((ss) => {
                    if (/xref/.test(ss.content)) {
                      hasXref = true;
                    }
                    if (/s3:/.test(ss.content)) {
                      hasAttach = true;
                    }
                  });
                }
              });
            }
            if (hasXref) console.log('has crossref', p.key, p.title);
            if (hasAttach) console.log('has attach', p.key, p.title);
          });
        }
      });
    }
  }, [manualQuery.data]);

  return manualQuery.isFetching || manualQuery.isLoading ? (
    <Loading />
  ) : (
    <TakeoverLayout
      backUrl='..'
      pageTitle={manualQuery.data?.name || ''}
      headerProps={{
        title: manualQuery.data?.name || '',
        breadcrumbItems: [
          <Link
            key='b1'
            href='..'
          >
            {t('policy.issuedManuals.title')}
          </Link>,
          <Typography key='b2'>{manualQuery.data?.name || ''}</Typography>,
        ],
      }}
    >
      <ListSection
        columns={columns}
        data={tocItems}
        hideHeaders
        query={manualQuery}
        showChrome={true}
      />
      {selectedContent && (
        <ExportModal
          open={exportModalOpen}
          onClose={() => {
            setExportModalOpen(false);
            setSelectedContent(null);
          }}
          exportType='content'
          manualId={id!}
          contentId={selectedContent.id}
          contentType='PREFACE'
          contentTitle={selectedContent.title}
        />
      )}
    </TakeoverLayout>
  );
};

export default IssuedManualsToC;
